/* Verbatim port of the §1 IMPACT-view result popover from
 * service/review/index.html: recommendedMoveFor (~1807-1819), sparkSvg
 * (~1821-1838), closeResultPopover/openResultPopover (~1840-1930), and the
 * two document-level listeners that close it (~1931-1935).
 *
 * PARAMETERIZED (rule a): openResultPopover read the global `STATE`
 * (STATE.aiNarrative, STATE.impact, STATE.brand) — takes `state:
 * AppPayloadState` as an explicit first parameter, appended before the
 * function's original (url, ev, ctx) params per the drawer.ts/exec.ts
 * convention.
 *
 * PARAMETERIZED: module-local `FLAGGED` + loadFlags/saveFlags (~1803-1805)
 * were already ported by F2a into state/ui.ts's `flaggedStore` (same
 * ACT_STATUS-style Store treatment) — this file reads/writes through that
 * store instead of a bare array + its own localStorage calls.
 * `flaggedStore.get().push(...)` mutates the store's live array in place
 * (matching the original's in-place `FLAGGED.push(...)`) rather than
 * replacing it via `.set()`; `flaggedStore.save(brand)` persists exactly
 * like the original's saveFlags().
 *
 * PARAMETERIZED: `SERP_TERM` (read only, to match the "search story"
 * combo to the currently-viewed term) becomes getSerpTerm() (views/
 * search.ts) — see that file's module doc for why.
 *
 * The `/impact/url` fetch goes through api/impact.ts's fetchUrlDetail()
 * (F2a), brand stripped of any " (..." suffix per the old call site
 * (~1900, same stripping loadTimeline used, not mvLoad's raw brand).
 *
 * DEVIATION: the original computed `const others = data.combos.filter(c2
 * => c2 !== main);` (~1911) but never read it anywhere afterward — a pure,
 * side-effect-free dead computation. Dropped here (TS `noUnusedLocals`);
 * behavior-neutral, nothing downstream ever referenced it.
 *
 * F3 (wave B): `pop.innerHTML = ...` → `render(html\`...\`, pop)` (the
 * render target is the freshly-created `pop` div, not a pre-existing
 * getElementById() element — render() works identically against a
 * detached node; `document.body.appendChild(pop)` still runs after).
 * `#rp-story`'s own later `box.innerHTML = ...` (the async layer) becomes
 * its own render() call, same sub-render granularity as every other view.
 *
 * F4 item 2 FIX (render()-doesn't-clear bug, same class the F3c report
 * found on views/trend.ts's #trendMeta / views/timeline.ts's #tlGrid):
 * `#rp-story` used to carry its "reading IMPACT history…" loading text as
 * STATIC markup nested inside the outer `render(html..., pop)` call above,
 * with the async layer's OWN independent `render()` call (previous
 * paragraph) targeting `#rp-story` directly for the first time once the
 * fetch resolved. Because `render()`'s first-ever call against a container
 * APPENDS after whatever's already there rather than clearing it (confirmed
 * against lit-html's own render() — "The first render will append the
 * rendered value to the container"), the loading text was never replaced —
 * it stayed permanently, with the real "Search story"/error content
 * stacking after it on every popover open. Fixed by leaving `#rp-story`
 * EMPTY in the outer template and giving it its OWN first render() call
 * right after `document.body.appendChild(pop)` (same "own render() call for
 * an empty template hook" convention ws/board.ts's toggleDensity button
 * uses) — every later write into `#rp-story` (the async layer's three
 * branches) is now that SAME part's second-or-later `_$setValue()`, which
 * correctly replaces instead of appending.
 *
 * `chip()` → `chipT()`, building the `chips` array with `nothing` in place
 * of `''` for the empty branches and dropping the `.filter(Boolean).join(
 * '')` (lit renders an array with `nothing` entries as those entries
 * producing no output, same visible result).
 *
 * Literal `onclick="..."` attributes (the close button, "Full asset
 * record", "Copy URL") convert to `@click`. "Full asset record" called the
 * `openDrawer(id)` window shim (id-only, single-arg — main.ts supplied
 * `state` from payloadStore.get()); this file now imports openDrawer
 * directly (ui/drawer.ts) and calls it with the `state` it already has as
 * a parameter — no import cycle (drawer.ts doesn't import popover.ts,
 * checked). `closeResultPopover()` calls itself directly (same module, no
 * import needed) instead of through the window global. Both onclick
 * strings were this global's ONLY consumers in the whole codebase
 * (grepped repo-wide, `closeResultPopover` and `openDrawer` alike), so
 * main.ts's shim entries for both are removed (see the F3b report's
 * checklist) — `closeDrawer`'s shim entry is untouched: it's consumed by
 * the STATIC `hub/index.html` shell's own close button
 * (`<button class="close" onclick="closeDrawer()">`), not by any
 * lit-converted template, so it stays.
 *
 * The `#rp-flag` button's post-render `flagBtn.onclick = () => {...;
 * flagBtn.textContent = '...';}` wiring (it mutates its OWN text after a
 * click, not a full re-render) moves into `@click` directly, using
 * `ev.currentTarget` in place of the closed-over `flagBtn` reference — the
 * `if (flagBtn)` null-guard the original needed (because
 * `getElementById('rp-flag')` could miss) is no longer needed since the
 * button — when this branch renders at all — is always the click target.
 *
 * SELF-CORRECTION (avoiding unsafeHTML, same call F3a made for entity.ts's
 * kpWord/chainClause): `sparkSvg()` (a first-party helper that built an
 * inline `<svg>` HTML STRING from numeric series data) is renamed
 * `sparkT()` and rebuilt to return a TemplateResult (`html\`<svg>...\`` —
 * nested SVG markup renders fine inside a plain `html` template when it's
 * not the render() root) instead of a string — no unsafeHTML needed for
 * the search-story sparkline.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { fetchUrlDetail } from '../api/impact.ts';
import { assetForUrl } from '../derive/exec.ts';
import { CAT_LABEL, canEdit } from '../derive/axes.ts';
import { domainOf, faviconUrl, fixMojibake } from '../lib/format.ts';
import { chipT } from '../lib/templates.ts';
import { flaggedStore } from '../state/ui.ts';
import { openDrawer } from './drawer.ts';
import { getSerpTerm } from '../views/search.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

/** Context the click site already knows about the result (all optional —
 * dataset reads and trend rows pass different subsets). */
export interface ResultPopoverCtx {
  rating?: string | null;
  ownership?: string | null;
  title?: string | null;
  city?: string | null;
  term?: string | null;
  location?: string | null;
}

/** {d: date, pos: position} points from a UrlCombo.series */
interface SparkPoint {
  d?: string;
  pos: number;
  [k: string]: unknown;
}

function recommendedMoveFor(domain: string | null, asset: NormalizedAsset | null, rating: string | null | undefined): string {
  const dom = (domain || '').toLowerCase();
  const controlled = asset && (asset.category === 'owned' || canEdit(asset));
  const neg = rating === 'negative' ? 'Undesirable on page one — ' : '';
  if (controlled) return 'Client-controlled: keep it complete, current and cross-linked — it defends this slot against everything below it.';
  if (/wikipedia|wikidata/.test(dom)) return neg + 'pursue via the COI-compliant Wikipedia/Wikidata workstream: sourced Talk-page edit requests, never direct edits — accuracy here propagates to every engine.';
  if (/trustpilot|tripadvisor|skytrax|airlinequality|glassdoor|yelp|kayak|indeed/.test(dom)) return neg + 'review platform: claim the profile, respond to reviews, and invite all customers to share feedback — its rating is what searchers and AI engines read.';
  if (/reuters|jpost|timesofisrael|globes|bloomberg|forbes|haaretz|news|bbc|cnn/.test(dom)) return neg + 'earned media: not editable — pitch corrective or fresh coverage to the same outlet, and make the owned newsroom rank for the same stories.';
  if (/finance|investing|tradingview|stockanalysis|simplywall|marketscreener/.test(dom)) return neg + 'financial-data site: keep IR filings and the investor site complete so aggregators (and engines) pull correct numbers automatically.';
  if (/skyscanner|expedia|opodo|edreams|kiwi|cheapflights|travelup|wego|trip\.com/.test(dom)) return neg + 'travel aggregator: largely feed-driven — verify the inventory/branding it shows and pursue partner-channel corrections for anything stale.';
  if (/google\.|play\.google|apps\.apple/.test(dom)) return 'platform surface: manage through its owner console (Business Profile / app store listing) — claiming and completeness are the levers.';
  return neg + 'assess the source\'s authority; if it matters, claim or engage it directly, and build a stronger owned page for the same intent to compete for this slot.';
}

function sparkT(series: SparkPoint[] | null | undefined, height?: number): TemplateResult {
  if (!series || series.length < 2) return html`<div class="caption">not enough polls for a trend</div>`;
  const H = height || 64, W = 396, padT = 6, padB = 14;
  const maxPos = Math.max(12, ...series.map((p) => p.pos));
  const y = (pos: number) => padT + ((pos - 1) / (maxPos - 1)) * (H - padT - padB);
  const x = (i: number) => 4 + (i / (series.length - 1)) * (W - 8);
  const pts = series.map((p, i) => `${x(i).toFixed(1)},${y(p.pos).toFixed(1)}`).join(' ');
  const p1y = y(10);
  const first = series[0], last = series[series.length - 1];
  if (!first || !last) return html`<div class="caption">not enough polls for a trend</div>`; // length >= 2 above — narrows for TS
  return html`<svg viewBox="0 0 ${W} ${H}" style="width:100%;height:${H}px;display:block">
    <line x1="4" y1="${p1y}" x2="${W-4}" y2="${p1y}" stroke="var(--imp-notowned)" stroke-width="1" stroke-dasharray="3 4"/>
    <text x="${W-6}" y="${p1y - 3}" text-anchor="end" font-size="8" fill="var(--muted)">page 1 ↑</text>
    <polyline points="${pts}" fill="none" stroke="var(--imp-owned)" stroke-width="1.8" stroke-linejoin="round"/>
    <circle cx="${x(series.length-1)}" cy="${y(last.pos)}" r="3" fill="var(--imp-owned)"/>
    <text x="4" y="${H-3}" font-size="8" fill="var(--muted)">${first.d}</text>
    <text x="${W-6}" y="${H-3}" text-anchor="end" font-size="8" fill="var(--muted)">${last.d} · #${last.pos}</text>
  </svg>`;
}

export function closeResultPopover(): void { const p = document.getElementById('rpop'); if (p) p.remove(); }

export async function openResultPopover(state: AppPayloadState, url: string, ev: MouseEvent, ctx: ResultPopoverCtx | null): Promise<void> {
  closeResultPopover();
  if (!url) return;
  const asset = assetForUrl(state, url);
  const dom = domainOf(url);
  const pop = document.createElement('div');
  pop.className = 'rpop'; pop.id = 'rpop';
  // DOCUMENT-anchored (absolute, page coordinates) so the popover sits beside
  // the clicked card and scrolls WITH it; long content scrolls inside .rpop
  const anchor: { right: number; top: number; left: number; bottom: number } =
    (ev.currentTarget && (ev.currentTarget as Element).getBoundingClientRect)
    ? (ev.currentTarget as Element).getBoundingClientRect()
    : { right: ev.clientX, top: ev.clientY, left: ev.clientX, bottom: ev.clientY };
  let x = anchor.right + 10 + window.scrollX;
  if (x + 450 > window.scrollX + document.documentElement.clientWidth)
    x = Math.max(10 + window.scrollX, anchor.left + window.scrollX - 450);
  const y = Math.max(10 + window.scrollY, anchor.top + window.scrollY - 60);
  pop.style.left = x + 'px';
  pop.style.top = y + 'px';
  // instant layer: identity + planes we already hold client-side
  const aiDom = (state.aiNarrative?.citedDomains || []).find((d2) => d2.domain === dom);
  const dur = state.impact && (state.impact.urlDurability || {})[url];
  const rating = ctx && ctx.rating;
  const chips = [
    rating && rating !== 'unrated' ? chipT(rating === 'positive' ? 'Desirable' : rating === 'negative' ? 'Undesirable' : 'Neutral', rating === 'negative' ? 'bad' : rating === 'positive' ? 'ok' : '') : nothing,
    ctx && ctx.ownership ? chipT(ctx.ownership === 'owned' ? 'Owned' : ctx.ownership === 'resembler' ? 'Not Me' : 'Not Owned', ctx.ownership === 'owned' ? 'info' : '') : nothing,
    asset ? chipT(CAT_LABEL[asset.category], asset.category) : chipT('not in the asset map', ''), // R10: unmapped ≠ problem → neutral
    asset && asset.needsReview ? chipT('⚑ review', 'warn') : nothing,
    dur ? chipT(`${dur.durability} · avg #${dur.meanPosition}`, '') : nothing,
  ];
  const pf = asset && asset.pageFacts;
  const flagged = flaggedStore.get().some((f) => f.url === url);

  render(html`
    <button class="rp-close" @click=${() => closeResultPopover()}>×</button>
    <div class="rp-head"><img src="${faviconUrl(url, 64)}" alt="">
      <div><div class="rp-title">${fixMojibake((ctx && ctx.title) || (asset && asset.title) || dom || url)}</div>
      <a class="rp-url" href="${url}" target="_blank" rel="noopener">${url} ↗</a></div></div>
    <div class="rp-chips">${chips}</div>
    <div id="rp-story"></div>
    ${aiDom ? html`<div class="rp-sec">AI narrative</div>
      <div class="rp-presence">Also feeds the AI engines: <b>${aiDom.count} citations</b> across ${aiDom.models || '?'} engines this month (${aiDom.count > (aiDom.compareCount ?? aiDom.count) ? '▲ +' + (aiDom.count - (aiDom.compareCount ?? aiDom.count)) : aiDom.count < (aiDom.compareCount ?? aiDom.count) ? '▼ ' + ((aiDom.compareCount ?? aiDom.count) - aiDom.count) : 'flat'} vs prior).</div>` : nothing}
    ${pf ? html`<div class="rp-sec">Crawler</div><div class="rp-presence">${pf.fetched ? `Fetched${pf.fetchedVia && pf.fetchedVia !== 'static' ? ' via ' + pf.fetchedVia : ''}${pf.status && pf.status !== 200 ? ` (direct fetch returned HTTP ${pf.status})` : ''}${(pf.linksToMap||[]).length ? ` · links out to ${(pf.linksToMap||[]).length} mapped assets` : ' · no links to mapped assets observed'}` : (pf.reason || 'not fetched')}</div>` : nothing}
    <div class="rp-sec">Recommended move</div>
    <div class="rp-move">${recommendedMoveFor(dom, asset, rating)}</div>
    <div class="rp-foot">
      ${asset ? html`<button class="exportbtn" @click=${() => { closeResultPopover(); openDrawer(state, asset.id); }}>Full asset record</button>`
              : html`<button class="exportbtn" id="rp-flag" @click=${(clickEv: MouseEvent) => {
                if (!flaggedStore.get().some((f) => f.url === url)){ flaggedStore.get().push({url, title: (ctx && ctx.title) || dom, at: new Date().toISOString().slice(0,10)}); flaggedStore.save(state.brand || ''); }
                (clickEv.currentTarget as HTMLElement).textContent = '✓ flagged for mapping';
              }}>${flagged ? '✓ flagged for mapping' : '⚑ Flag for mapping'}</button>`}
      <button class="exportbtn" @click=${() => { navigator.clipboard && navigator.clipboard.writeText(url); }}>Copy URL</button>
    </div>`, pop);
  document.body.appendChild(pop);
  // #rp-story's OWN first render() call (F4 item 2 fix — see the module doc
  // comment): establishing its lit part here, on an element the outer
  // template above left EMPTY, means the async layer's later render() calls
  // into this same element correctly replace this loading message instead
  // of stacking after it. Calling render() with `pop` (a nested static
  // element in the outer template) as the FIRST-EVER target would not have
  // cleared this loading text — lit only manages its own marker-anchored
  // part, so a later independent render() into an element that already
  // carries non-lit content APPENDS after it instead of replacing it (same
  // root cause as the F3c tlGrid/trendMeta finding, just one level deeper:
  // the stale content here was never a plain textContent/innerHTML write,
  // it was static markup from the OUTER render() call above).
  render(html`<div class="rp-loading">reading IMPACT history… <span style="color:var(--muted)">(the first look at a client loads the full history — up to a minute; instant after that)</span></div>`,
    pop.querySelector('#rp-story') as HTMLElement);

  // async layer: the full search story from the cached history
  try {
    const data = await fetchUrlDetail((state.brand || '').split(' (')[0] ?? '', url);
    const box = document.getElementById('rp-story');
    if (!box) return;
    const combos = data.combos || [];
    if (!data.available || !combos.length){
      render(html`<div class="rp-sec">Search story</div><div class="rp-presence">${data.reason || data.note || 'no tracked history for this exact URL'}${dur ? ` — but its domain averages #${dur.meanPosition} over ${dur.polls} polls (${dur.durability}).` : ''}</div>`, box);
      return;
    }
    const ctxCity = ctx && ctx.city;
    const main = combos.find((c2) => c2.term === getSerpTerm() && (!ctxCity || c2.location === ctxCity)) || combos[0];
    if (!main) return; // combos non-empty (checked above) — narrows for TS
    const cities = combos.filter((c2) => c2.term === main.term);
    const terms = [...new Set(combos.map((c2) => c2.term))];
    render(html`
      <div class="rp-sec">Search story — "${main.term}" · ${main.location || ''}</div>
      <div class="rp-stats">
        <div class="rp-stat"><div class="n">#${main.best}</div><div class="l">best position</div></div>
        <div class="rp-stat"><div class="n">#${main.mean}</div><div class="l">average (±${main.stdev})</div></div>
        <div class="rp-stat"><div class="n">${main.presencePct}%</div><div class="l">of ${main.totalPollDays} polled days</div></div>
        <div class="rp-stat"><div class="n">${(main.firstSeen || '').slice(5)}</div><div class="l">first seen</div></div>
      </div>
      <div class="rp-spark">${sparkT(main.series)}</div>
      ${main.ratingDrift || main.ownershipDrift ? html`<div class="rp-presence" style="margin-top:6px;color:var(--amber-tx)">markup changed during the window${main.ratingDrift ? ` · rating: ${main.ratingDrift.join(' → ')}` : ''}${main.ownershipDrift ? ` · ownership: ${main.ownershipDrift.join(' → ')}` : ''}</div>` : nothing}
      <div class="rp-sec">Presence</div>
      <div class="rp-presence">Ranks in <b>${cities.length}</b> ${cities.length === 1 ? 'city' : 'cities'} on "${main.term}"${cities.length > 1 ? ` (${cities.map((c2) => `${c2.location || '—'} #${c2.mean}`).join(' · ')})` : ''}${terms.length > 1 ? ` · also tracked on ${terms.length - 1} other term${terms.length > 2 ? 's' : ''}` : ''}.</div>`, box);
  } catch(e){
    const box = document.getElementById('rp-story');
    if (box) render(html`<div class="rp-presence">service not reachable — the search story needs :8000${dur ? `. Cached: avg #${dur.meanPosition} over ${dur.polls} polls (${dur.durability}).` : '.'}</div>`, box);
  }
}

export function initPopoverGlobalListeners(): void {
  document.addEventListener('click', e => {
    const p = document.getElementById('rpop');
    if (p && !p.contains(e.target as Node) && !(e.target as Element).closest('.imp-result')) closeResultPopover();
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeResultPopover(); });
}
